import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    IconButton,
    Chip,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Book,
    Edit3,
    Trash2,
    Users,
    Bot,
    Lock,
    Globe,
    CheckCircle,
    Clock
} from 'lucide-react';

const CourseCard = ({ course, onDelete, onManageAccess, onAttachTutor }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <div className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-all shadow-sm">
            <div className="flex justify-between items-start">
                <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        <Book className="text-blue-500 w-5 h-5" />
                        <span 
                            className="cursor-pointer hover:text-blue-600"
                            onClick={() => navigate(`/courses/${course.id}`)}
                        >
                            {course.title}
                        </span>
                    </h3>
                    
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Chip
                            label={course.is_published ? t('dashboard.courses.status.published') : t('dashboard.courses.status.draft')}
                            size="small"
                            className={course.is_published ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}
                            icon={course.is_published ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                        />
                        
                        <Chip
                            label={course.is_private ? t('dashboard.courses.status.private') : t('dashboard.courses.status.public')}
                            size="small"
                            className={course.is_private ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}
                            icon={course.is_private ? <Lock className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
                        />
                        
                        {course.students_count !== undefined && (
                            <Chip
                                label={t('dashboard.courses.students') + ': ' + course.students_count}
                                size="small"
                                className="bg-gray-100 text-gray-700"
                                icon={<Users className="w-3.5 h-3.5" />}
                            />
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <Tooltip title={t('dashboard.courses.edit')}>
                        <IconButton
                            size="small"
                            color="info"
                            onClick={() => navigate(`/courses/${course.id}/edit`)}
                        >
                            <Edit3 className="w-4 h-4" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('dashboard.courses.manage_access')}>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onManageAccess(course)}
                        >
                            <Users className="w-4 h-4" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('dashboard.courses.attach_tutor')}>
                        <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => onAttachTutor(course)}
                        >
                            <Bot className="w-4 h-4" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('dashboard.courses.delete')}>
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(course, 'course')}
                        >
                            <Trash2 className="w-4 h-4" />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            {course.description && (
                <Typography variant="body2" className="text-gray-600 mt-3 line-clamp-2">
                    {course.description}
                </Typography>
            )}
        </div>
    );
};

export default CourseCard;